/**
 * TaskKanbanBoard - Board principal do Kanban de Tarefas
 * 
 * Gerencia drag & drop entre colunas (fases) e orquestra filtros.
 * Segue padrão do KanbanBoard (Conversas) adaptado para tarefas.
 * 
 * @version 1.5.0
 * RELEASE: 2026-10-05 v1.5.0 - Categoria ativa sincronizada com o parâmetro
 *          `category` da URL para suportar atalhos diretos do menu lateral,
 *          navegação voltar/avançar e fallback seguro para categorias válidas.
 * RELEASE: 2026-10-02 v1.4.0 - Setinhas de navegação entre colunas (ScrollIndicators)
 *          e rolagem sem encadear com a página, igual ao Kanban de Negócios.
 *          Vale para Tarefas, Jurídico e Retenção (quadro compartilhado).
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-08 v1.1.0 - Permissões granulares por fase com isResponsible
 * RELEASE: 2026-08-06 v1.2.0 - Paridade com o Kanban de Negócios: coluna
 *          "Nova Fase", reordenação de colunas por arraste, edição rápida da
 *          fase (lápis) e criação de tarefa direto na fase (+)
 * RELEASE: 2026-08-06 v1.3.0 - Colisão separada por tipo de arraste: fases
 *          consideram somente sortables `column-*`, sem interferência dos cards
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { getTaskModuleMenuKeys } from '@/lib/tasks/taskModuleMenuKeys';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';
import { TaskCreatedSuccessDialog } from '@/components/tasks/modal/TaskCreatedSuccessDialog';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  closestCenter,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import { InteractiveKanbanBoard } from '@/components/kanban/InteractiveKanbanBoard';
import { SortableCategoryTab } from '@/components/kanban/SortableCategoryTab';
import { kanbanCollision } from '@/components/kanban/kanbanCollision';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsBar } from '@/components/ui/scrollable-tabs-bar';

import { cn } from '@/lib/utils';
import { TaskKanbanColumn } from './TaskKanbanColumn';
import { AddTaskPhaseColumn, type TaskPhaseFormValues } from './AddTaskPhaseColumn';
import { TaskKanbanFilters } from './TaskKanbanFilters';
import { TaskCard } from './TaskCard';
import { useTaskKanban, type TaskKanbanFilters as FilterType, type TaskKanbanItem } from '@/hooks/useTaskKanban';
import { useTaskKanbanCounts, type TaskPhaseCount } from '@/hooks/useTaskKanbanCounts';
import { useInvalidateTaskKanbanColumns } from '@/hooks/useTaskKanbanColumn';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { useTaskPhaseAutomations } from '@/hooks/useTaskPhaseAutomations';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useTaskCategories } from '@/hooks/useTaskCategories'; // v1.3.0: reordenação das abas

import { useReorderTaskPhases } from '@/hooks/useReorderTaskPhases';
import { TaskAutomationModal } from './TaskAutomationModal';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { KanbanCategorySelect } from '@/components/kanban/KanbanCategorySelect';
import { ClipboardList } from 'lucide-react';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('TaskKanbanBoard');

export function TaskKanbanBoard() {

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Módulo corrente — mantém o atalho de visualização dentro de Tarefas/Retenção/Jurídico
  const taskModule = useTaskModule();
  const { canCreate: canCreateMenu, canEdit: canEditMenu } = usePermissions();
  // 2026-10-22: permissões do próprio módulo (Retenção/Jurídico não dependem de tasks_*)
  const moduleKeys = getTaskModuleMenuKeys(taskModule.type);
  // Filtros ativos
  const [filters, setFilters] = useState<FilterType>({});
  // Tarefa sendo arrastada
  const [activeTask, setActiveTask] = useState<TaskKanbanItem | null>(null);
  // v1.2.0 - Coluna (fase) sendo arrastada + ordem visual temporária
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [localPhaseOrder, setLocalPhaseOrder] = useState<TaskPhaseCount[] | null>(null);
  // Estado do dialog de criação
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // v1.2.0 - Fase pré-selecionada ao criar tarefa pelo botão "+" da coluna
  const [createDialogPhaseId, setCreateDialogPhaseId] = useState<string | undefined>(undefined);
  // Tarefa selecionada para visualização
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  // Estado do modal de automações por fase (ícone de raio nos headers)
  const [automationModalOpen, setAutomationModalOpen] = useState(false);


  // Estado do modal de sucesso após criação
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);
  const [createdTaskNumber, setCreatedTaskNumber] = useState<number>(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Hook principal do Kanban
  const { categories, isLoadingCategories, moveTask } = useTaskKanban();

  // Usuário logado para contexto de isResponsible
  const { user: currentUser } = useAuth();

  // Preferência do usuário para categoria padrão (aba inicial do Kanban).
  // Só é aplicada antes de o usuário trocar manualmente de aba na sessão.
  const { data: userPrefs } = useUserPreferences();
  const preferredCategoryId = userPrefs?.default_task_category_id ?? null;
  const viewMode = userPrefs?.kanban_view_tasks ?? 'tabs';
  const fallbackCategoryId =
    (preferredCategoryId && categories.some((c) => c.id === preferredCategoryId)
      ? preferredCategoryId
      : null) ?? categories[0]?.id ?? null;

  // A URL tem prioridade somente quando aponta para uma categoria ativa e
  // acessível neste módulo; links inválidos usam a preferência/fallback atual.
  const requestedCategoryId = searchParams.get('category');
  const activeCategoryId =
    (requestedCategoryId && categories.some((category) => category.id === requestedCategoryId)
      ? requestedCategoryId
      : null) ?? fallbackCategoryId;

  /** Mantém URL, abas e seletor sincronizados sem recarregar a página. */
  const handleCategoryChange = useCallback((categoryId: string) => {
    if (!categories.some((category) => category.id === categoryId)) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('category', categoryId);
    setSearchParams(nextParams);
  }, [categories, searchParams, setSearchParams]);

  // Remove somente parâmetros inválidos depois que o catálogo estiver pronto.
  useEffect(() => {
    if (isLoadingCategories || !requestedCategoryId) return;
    if (categories.some((category) => category.id === requestedCategoryId)) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('category');
    setSearchParams(nextParams, { replace: true });
  }, [categories, isLoadingCategories, requestedCategoryId, searchParams, setSearchParams]);

  // ==========================================================================
  // v1.3.0 - Reordenação das abas de categorias (drag and drop)
  // ==========================================================================
  const { reorder: reorderCategories } = useTaskCategories(true);
  // Só reordena quem pode editar categorias do módulo de tarefas
  const canReorderCategories = canEditMenu('tasks_categories') && categories.length > 1;

  // Ordem local das abas (feedback otimista durante o arraste)
  const [orderedCategories, setOrderedCategories] = useState(categories);
  useEffect(() => {
    setOrderedCategories(categories);
  }, [categories]);

  // Sensores exclusivos das abas (não interferem no DnD do board)
  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Aplica nova ordem das abas e persiste (rollback em caso de erro)
  const handleCategoryDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedCategories.findIndex((c) => c.id === active.id);
    const newIndex = orderedCategories.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const previous = orderedCategories;
    const nova = arrayMove(orderedCategories, oldIndex, newIndex);
    setOrderedCategories(nova);

    try {
      await reorderCategories(nova.map((c) => c.id));
    } catch (error) {
      logger.error('Erro ao reordenar categorias do Kanban de tarefas', { error });
      setOrderedCategories(previous);
    }
  };



  // Hook de contagens por fase
  const { phases: phaseCounts, countsMap, invalidateCounts } = useTaskKanbanCounts(activeCategoryId, filters);

  // Invalidar todas as colunas
  const invalidateAll = useInvalidateTaskKanbanColumns();

  // Hook de permissões granulares por fase
  const { canDoOnPhase } = useTaskPhasePermissions(activeCategoryId);

  // Hook de automações por fase (raio no header + modal)
  const { hasAutomation, automationCount } = useTaskPhaseAutomations(activeCategoryId);

  // Lista de fases no shape esperado pelo TaskAutomationModal
  const automationPhases = useMemo(
    () =>
      phaseCounts.map((p) => ({
        id: p.phase_id,
        name: p.phase_name,
        color: p.phase_color,
      })),
    [phaseCounts],
  );

  /**
   * Helper: verifica se o usuário logado é o responsável pela tarefa
   */
  const isResponsibleFor = useCallback((task: TaskKanbanItem | null): boolean => {
    if (!task || !currentUser?.id) return false;
    return task.assigned_to === currentUser.id;
  }, [currentUser?.id]);

  /**
   * Verifica se o usuário tem permissão can_create em pelo menos uma fase
   */
  const canCreateInAnyPhase = useMemo(() => {
    return phaseCounts.some((phase) => canDoOnPhase(phase.phase_id, 'can_create'));
  }, [phaseCounts, canDoOnPhase]);

  // ==========================================================================
  // v1.2.0 - Gestão de fases direto no board (criar / editar / reordenar)
  // ==========================================================================
  // Permissões de menu para manutenção de fases (validadas também na RPC)
  const canCreatePhase = canCreateMenu(moduleKeys.phases);
  const canEditPhase = canEditMenu(moduleKeys.phases);

  // Mutations de fase (upsert_task_phase) e reordenação (reorder_task_phases)
  const { upsert: upsertPhase, isUpserting } = useTaskPhases(activeCategoryId || undefined);
  const reorderPhases = useReorderTaskPhases(activeCategoryId);

  // Fases visíveis (permissão can_view ou com tarefas visíveis por bypass criador)
  const visiblePhases = useMemo(
    () => phaseCounts.filter((phase) => canDoOnPhase(phase.phase_id, 'can_view') || phase.total_count > 0),
    [phaseCounts, canDoOnPhase],
  );

  // Ordem exibida: preview local durante o arraste > ordem do servidor
  const displayPhases = localPhaseOrder ?? visiblePhases;

  // IDs prefixados para o SortableContext das colunas
  const columnIds = useMemo(
    () => displayPhases.map((p) => `column-${p.phase_id}`),
    [displayPhases],
  );

  /** Cria uma nova fase na categoria ativa */
  const handleCreatePhase = useCallback(async (values: TaskPhaseFormValues) => {
    if (!activeCategoryId) {
      toast.error('Selecione uma categoria antes de criar a fase');
      return;
    }
    await upsertPhase({
      p_category_id: activeCategoryId,
      p_name: values.name,
      p_color: values.color,
      p_is_final: values.isFinal,
      p_sort_order: phaseCounts.length,
    });
    invalidateCounts();
    invalidateAll();
  }, [activeCategoryId, upsertPhase, phaseCounts.length, invalidateCounts, invalidateAll]);

  /** Edita rapidamente uma fase existente (nome, cor, fase final) */
  const handleEditPhase = useCallback(async (phase: TaskPhaseCount, values: TaskPhaseFormValues) => {
    if (!activeCategoryId) return;
    await upsertPhase({
      p_id: phase.phase_id,
      p_category_id: activeCategoryId,
      p_name: values.name,
      p_color: values.color,
      p_is_final: values.isFinal,
    });
    invalidateCounts();
    invalidateAll();
  }, [activeCategoryId, upsertPhase, invalidateCounts, invalidateAll]);



  // Sensores para drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /**
   * Separa os alvos de colisão conforme o tipo arrastado.
   * Fases devem colidir somente com `column-*`; caso contrário, cards e áreas
   * internas vencem a colisão e o contexto horizontal não abre espaço.
   */
  const collisionDetectionStrategy = kanbanCollision;

  // Início do arraste (card de tarefa OU coluna/fase)
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeData = event.active.data.current;

    // v1.2.0 - Arraste de coluna: inicializa a ordem visual temporária
    if (activeData?.type === 'column') {
      setActiveColumnId(activeData.phaseId as string);
      setActiveTask(null);
      setLocalPhaseOrder([...visiblePhases]);
      return;
    }

    const taskData = activeData?.task;
    if (taskData) {
      setActiveColumnId(null);
      setActiveTask(taskData);
    }
  }, [visiblePhases]);

  // v1.2.0 - Reordena colunas visualmente durante o arraste
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;
    if (activeData?.type !== 'column') return;

    const activePhaseId = activeData.phaseId as string;
    const overId = String(over.id);
    // A estratégia de colisão garante que fases recebam apenas alvos de fase.
    if (!overId.startsWith('column-')) return;
    const overPhaseId = overId.replace('column-', '');

    if (!overPhaseId || activePhaseId === overPhaseId) return;

    setLocalPhaseOrder((prev) => {
      if (!prev) return prev;
      const oldIndex = prev.findIndex((p) => p.phase_id === activePhaseId);
      const newIndex = prev.findIndex((p) => p.phase_id === overPhaseId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  /** Restaura o board quando o arraste é cancelado. */
  const handleDragCancel = useCallback(() => {
    setActiveTask(null);
    setActiveColumnId(null);
    setLocalPhaseOrder(null);
  }, []);

  // Fim do arraste - move tarefa entre fases (ou persiste ordem das colunas)
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    const activeData = active.data.current;
    const overData = over?.data.current;

    // v1.2.0 - Captura a ordem visual final antes de limpar o estado
    const finalOrder = localPhaseOrder ? [...localPhaseOrder] : null;

    setActiveTask(null);
    setActiveColumnId(null);
    setLocalPhaseOrder(null);

    if (!over) return;
    const overId = over.id as string;

    // ------------------------------------------------------------------
    // v1.2.0 - Persistência da reordenação de colunas (fases)
    // ------------------------------------------------------------------
    if (activeData?.type === 'column') {
      if (!finalOrder) return;
      const activePhaseId = activeData.phaseId as string;
      const originalIndex = visiblePhases.findIndex((phase) => phase.phase_id === activePhaseId);
      const currentIndex = finalOrder.findIndex((phase) => phase.phase_id === activePhaseId);
      let effectiveOrder = finalOrder;

      // Solturas rápidas podem ocorrer antes da última atualização do onDragOver.
      if (originalIndex !== -1 && currentIndex === originalIndex) {
        const overIdString = String(over.id);
        const targetPhaseId = overIdString.startsWith('column-')
          ? overIdString.replace('column-', '')
          : overData?.type === 'column'
            ? (overData.phaseId as string)
            : null;
        const targetIndex = targetPhaseId
          ? finalOrder.findIndex((phase) => phase.phase_id === targetPhaseId)
          : -1;

        if (targetIndex !== -1 && targetIndex !== originalIndex) {
          effectiveOrder = arrayMove(finalOrder, originalIndex, targetIndex);
        }
      }

      const finalIndex = effectiveOrder.findIndex((phase) => phase.phase_id === activePhaseId);
      if (originalIndex !== -1 && finalIndex !== originalIndex) {
        logger.info('Persistindo reordenação de fases de tarefas');
        reorderPhases.mutate(
          effectiveOrder.map((phase, index) => ({
            phase_id: phase.phase_id,
            sort_order: index,
          })),
        );
      }
      return;
    }



    // ID da tarefa arrastada
    const taskId = active.id as string;
    const sourcePhaseId: string | undefined = activeData?.task?.phase_id;

    // Determina fase de destino — padrão DealKanbanBoard (tolerante a drop em
    // qualquer ponto da coluna: header, área vazia ou sobre outro card)
    let targetPhaseId: string | null = null;

    if (overData?.type === 'phase' && overData?.phaseId) {
      // Soltou sobre o droppable da coluna
      targetPhaseId = overData.phaseId as string;
    } else if (overData?.type === 'task' && overData?.task?.phase_id) {
      // Soltou sobre outro card → usa a fase desse card
      targetPhaseId = overData.task.phase_id as string;
    } else {
      // Fallback: over.id pode ser o próprio phase_id ou o id prefixado da coluna
      const rawId = overId.startsWith('column-') ? overId.replace('column-', '') : overId;
      const isPhase = phaseCounts.some((p) => p.phase_id === rawId);
      if (isPhase) targetPhaseId = rawId;
    }


    if (!targetPhaseId) return;
    // Mesmo lugar de origem → não dispara request (sem toast)
    if (targetPhaseId === sourcePhaseId) return;

    // Verificar permissão de can_change_phase na fase de ORIGEM (com contexto de responsável)
    if (sourcePhaseId) {
      const taskItem = activeData?.task as TaskKanbanItem | undefined;
      const isResponsible = isResponsibleFor(taskItem || null);
      if (!canDoOnPhase(sourcePhaseId, 'can_change_phase', isResponsible)) {
        toast.error('Sem permissão para mover tarefas desta fase');
        return;
      }
    }

    try {
      await moveTask({ taskId, newPhaseId: targetPhaseId });
      toast.success('Tarefa movida com sucesso');
      invalidateAll();
      invalidateCounts();
    } catch (err) {
      // Erro já tratado no hook
      logger.error('Erro ao mover tarefa via drag', err);
    }
  }, [moveTask, invalidateAll, invalidateCounts, canDoOnPhase, isResponsibleFor, phaseCounts, localPhaseOrder, visiblePhases, reorderPhases]);
  // Nome da categoria ativa para exibir no título
  const activeCategoryName = categories.find((c) => c.id === activeCategoryId)?.name || taskModule.label;

  // Handler de refresh para invalidar colunas e contagens
  const handleRefresh = useCallback(() => {
    invalidateAll();
    invalidateCounts();
  }, [invalidateAll, invalidateCounts]);

  // Se não há categorias
  if (!isLoadingCategories && categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-250px)] text-center">
        <p className="text-muted-foreground">
          Nenhuma categoria de tarefas encontrada. Crie uma categoria primeiro em Cadastros → Categorias.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
      {/* Header: Linha 1 - Título + Busca + Filtros + Nova Tarefa (padrão CRM) */}
      <div className="px-3 sm:px-4 py-3 border-b shrink-0 space-y-2 sm:space-y-3 w-full max-w-full">
        <TaskKanbanFilters
          categoryName={activeCategoryName}
          filters={filters}
          onFiltersChange={setFilters}
          onNewTask={canCreateInAnyPhase && canCreateMenu(moduleKeys.kanban) ? () => setShowCreateDialog(true) : undefined}
          onRefresh={handleRefresh}
          onToggleView={() => navigate(`${taskModule.basePath}/list`)}
          categoryId={activeCategoryId || undefined}
          titleSlot={
            viewMode === 'select' ? (
              // Modo seleção: dropdown de categoria substitui o título
              <KanbanCategorySelect
                icon={ClipboardList}
                categories={categories}
                selectedId={activeCategoryId}
                onChange={handleCategoryChange}
              />
            ) : undefined
          }
        />

        {/* Linha 2 - Tabs de categorias (padrão DealPipelineTabs). Ocultas no modo 'select'. */}
        {viewMode === 'tabs' && categories.length > 1 && (
          <Tabs
            value={activeCategoryId || ''}
            onValueChange={handleCategoryChange}
          >
            {/* Faixa rolável: barra horizontal + setas + touchpad para muitas categorias */}
            <ScrollableTabsBar activeKey={activeCategoryId} className="w-full">
              <TabsList className="h-9 w-max">
                {/* v1.3.0: abas reordenáveis por drag and drop (alça de 6 pontinhos) */}
                <DndContext
                  sensors={tabSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCategoryDragEnd}
                >
                <SortableContext
                  items={orderedCategories.map((c) => c.id)}
                  strategy={horizontalListSortingStrategy}
                >
                {orderedCategories.map((cat) => {
                  const isActive = activeCategoryId === cat.id;
                  return (
                    <SortableCategoryTab key={cat.id} id={cat.id} disabled={!canReorderCategories}>
                      <TabsTrigger
                        value={cat.id}
                        className={cn(
                          'px-3 sm:px-4 pr-7 sm:pr-8 text-xs sm:text-sm whitespace-nowrap',
                          canReorderCategories && 'pl-6'
                        )}
                        style={{
                          borderBottomColor: isActive ? cat.color : 'transparent',
                          borderBottomWidth: 2,
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </TabsTrigger>
                    </SortableCategoryTab>
                  );
                })}
                </SortableContext>
                </DndContext>
              </TabsList>
            </ScrollableTabsBar>

          </Tabs>
        )}

      </div>

      {/* Board com colunas */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* v1.4.0: setinhas de navegação + rolagem horizontal sem encadear */}
        <InteractiveKanbanBoard>
            {/* Colunas de fases (ordem visual durante arraste) dentro do */}
            {/* SortableContext horizontal — padrão DealKanbanBoard */}
            <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
              {displayPhases.map((phase) => (
                <TaskKanbanColumn
                  key={phase.phase_id}
                  phaseId={phase.phase_id}
                  phaseName={phase.phase_name}
                  phaseColor={phase.phase_color}
                  phaseIsFinal={phase.phase_is_final}
                  count={phase}
                  filters={filters}
                  onTaskClick={(task) => setSelectedTaskId(task.id)}
                  canDoOnPhase={canDoOnPhase}
                  currentUserId={currentUser?.id || null}
                  hasAutomation={hasAutomation(phase.phase_id)}
                  automationCount={automationCount(phase.phase_id)}
                  onAutomationClick={() => setAutomationModalOpen(true)}
                  // v1.2.0 - Ações de fase condicionadas às permissões
                  canReorderPhases={canEditPhase}
                  canEditPhase={canEditPhase}
                  onEditPhase={(values) => handleEditPhase(phase, values)}
                  canCreateTask={
                    canCreateMenu(moduleKeys.kanban) && canDoOnPhase(phase.phase_id, 'can_create')
                  }
                  onCreateTask={() => {
                    setCreateDialogPhaseId(phase.phase_id);
                    setShowCreateDialog(true);
                  }}
                />
              ))}

              {/* v1.2.0 - Coluna "Nova Fase" (só com permissão de criar fases) */}
              {activeCategoryId && (
                <AddTaskPhaseColumn
                  onAdd={handleCreatePhase}
                  isLoading={isUpserting}
                  canCreate={canCreatePhase}
                />
              )}
            </SortableContext>

            {/* Mensagem se não há fases */}
            {phaseCounts.length === 0 && !isLoadingCategories && (
              <div className="flex items-center justify-center w-full text-muted-foreground text-sm">
                Nenhuma fase cadastrada para esta categoria. Crie fases em Cadastros → Fases.
              </div>
            )}
        </InteractiveKanbanBoard>

        {/* Overlay durante arraste */}
        <DragOverlay
          dropAnimation={{
            duration: 250,
            easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
          }}
        >
          {activeTask && (
            <div className="rotate-3 scale-105">
              <TaskCard task={activeTask} />
            </div>
          )}

          {/* v1.2.0 - Preview da coluna arrastada */}
          {activeColumnId && (() => {
            const dragged = phaseCounts.find((p) => p.phase_id === activeColumnId);
            if (!dragged) return null;
            return (
              <div className="w-72 rounded-lg border border-primary/40 bg-background/95 shadow-xl p-3 rotate-2">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: dragged.phase_color || '#6b7280' }}
                  />
                  <span className="font-semibold text-sm truncate flex-1">{dragged.phase_name}</span>
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    {dragged.total_count}
                  </span>
                </div>
              </div>
            );
          })()}
        </DragOverlay>
      </DndContext>


      {/* Dialog de criação de tarefa */}
      <TaskFormDialog
        key={createDialogPhaseId ?? 'default'}
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          // Limpa a fase pré-selecionada ao fechar (v1.2.0)
          if (!open) setCreateDialogPhaseId(undefined);
        }}
        defaultCategoryId={activeCategoryId || undefined}
        defaultPhaseId={createDialogPhaseId}
        onSuccess={(taskId, taskNumber) => {
          setShowCreateDialog(false);
          setCreateDialogPhaseId(undefined);
          invalidateAll();
          invalidateCounts();
          // Exibir modal de sucesso com número da tarefa
          if (taskId && taskNumber) {
            setCreatedTaskId(taskId);
            setCreatedTaskNumber(taskNumber);
            setShowSuccessDialog(true);
          }
        }}
      />


      {/* Modal de sucesso após criação */}
      <TaskCreatedSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        taskNumber={createdTaskNumber}
        onViewTask={() => {
          setShowSuccessDialog(false);
          if (createdTaskId) {
            setSelectedTaskId(createdTaskId);
          }
        }}
      />

      {/* Modal de visualização/edição de tarefa */}
      <TaskDetailsModal
        taskId={selectedTaskId}
        open={!!selectedTaskId}
        onOpenChange={(open) => { if (!open) setSelectedTaskId(null); }}
        onDeleted={() => { setSelectedTaskId(null); invalidateAll(); invalidateCounts(); }}
        onNavigateToTask={(id) => setSelectedTaskId(id)}
      />

      {/* Modal de visualização das automações por fase (ícone de raio) */}
      <TaskAutomationModal
        open={automationModalOpen}
        onOpenChange={setAutomationModalOpen}
        categoryId={activeCategoryId}
        categoryName={activeCategoryName}
        phases={automationPhases}
      />
    </div>
  );
}
